/* eslint-disable react/forbid-prop-types, react/require-default-props */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

import { filterLiveKpis } from "../../../constants/kpi";
import { UseCompanyWorkspaces, UseCompanyColors, UseSelectedWorkspace } from "../../../hooks/company";
import { UseIsAdmin, UseUserId } from "../../../hooks/auth";
import { FETCH_BONUS_PROGRESS } from "../../../api/group";
import { widgetById } from "../../Homepage/customize/widgetRegistry";
import { realReps } from "../../../helpers/teamAverage";
import useWorkspaceData from "./useWorkspaceData";
import { IcDots, IcGrip, IcCopy, IcTrash, IcEdit, IcDashboard } from "./icons";

/**
 * A game component on an Analytics dashboard — same registry `render(ctx, s)`
 * path as the workspace Home board (ScopedBeatYeti / ScopedGameboard / etc.).
 * Board-wide Viewing (boardWs / inheritWorkspaceIds / boardWindow) is threaded
 * from DashboardBuilder so cross-workspace cards match Home.
 */

// Podium still follows the app's selected workspace (no scope:true in registry).
// KOTM is scope:true — unlocked from this set so ScopeWindow + inherit work.
const FOLLOWS_SELECTED = new Set(["podium"]);

const periodOf = (p) => {
  if (["today", "yesterday", "week", "lastWeek", "lastMonth", "month", "quarter", "ytd", "custom"].includes(p)) {
    return p;
  }
  if (p === "year") return "ytd";
  return "month";
};
// Builder period → Homepage periodIdx (0 yesterday · 1 today · 2 week · 3 month · 4 lastWeek · 5 lastMonth).
const PERIOD_IDX = {
  yesterday: 0, today: 1, week: 2, month: 3, lastWeek: 4, lastMonth: 5, quarter: 3, ytd: 3, year: 3, custom: 3,
};

export default function ComponentCard({
  widget,
  period,
  editing,
  onEdit,
  onDuplicate,
  onCopyToDashboard,
  onRemove,
  boardWs = null,
  boardWindow = null,
  boardCustom = null,
  inheritWorkspaceIds = [],
  saveWidgetSettings = null,
  boardWidgets = [],
}) {
  const workspaces = UseCompanyWorkspaces(true) || [];
  const isRealAdmin = UseIsAdmin();
  const userId = UseUserId() || null;
  const { primaryColor } = UseCompanyColors() || {};
  const selectedWorkspace = UseSelectedWorkspace();

  const def = widgetById(widget.comp);
  const opts = widget.options || {};
  const across = widget.comp === "leaderboard" && !!opts.across;
  const followsSelected = FOLLOWS_SELECTED.has(widget.comp);
  /* Host workspace for the card's own (non-scoped) reads. Falling straight to
     workspaces[0] made an unscoped card read whichever workspace the company
     happens to list first — the workspace the admin is actually in is the
     honest default. Scoped cards override this with options.workspaceIds. */
  const wsId = followsSelected
    ? selectedWorkspace?._id || null
    : opts.workspaceId || selectedWorkspace?._id || workspaces[0]?._id || null;
  const ws = workspaces.find((w) => String(w._id) === String(wsId)) || null;
  const fallbackWs = wsId ? [String(wsId)] : [];

  const needsWsData = ["beatyeti", "yeti", "earnings", "milestones", "annualcomp"].includes(widget.comp)
    || (typeof widget.comp === "string" && widget.comp.startsWith("custom-"));
  const data = useWorkspaceData(needsWsData ? wsId : null, {
    period: periodOf(period),
    custom: period === "custom" ? boardCustom : null,
  });

  const needsBonus = widget.comp === "earnings" || widget.comp === "milestones" || widget.comp === "annualcomp"
    || (typeof widget.comp === "string" && (widget.comp.startsWith("earnings#") || widget.comp.startsWith("milestones#") || widget.comp.startsWith("annualcomp#")));
  const [bonus, setBonus] = useState(null);
  useEffect(() => {
    if (!needsBonus || !wsId) return undefined;
    let alive = true;
    FETCH_BONUS_PROGRESS(wsId)
      .then((res) => {
        if (alive && res) {
          setBonus({ ...res, connected: !!(res.config && res.config.revenueKpiId) });
        }
      })
      .catch(() => alive && setBonus(null));
    return () => { alive = false; };
  }, [needsBonus, wsId]);

  const [menuAt, setMenuAt] = useState(null);
  const btnRef = useRef(null);
  const popRef = useRef(null);

  useEffect(() => {
    if (!menuAt) return undefined;
    const onDown = (e) => {
      if (
        popRef.current && !popRef.current.contains(e.target)
        && btnRef.current && !btnRef.current.contains(e.target)
      ) setMenuAt(null);
    };
    const close = () => setMenuAt(null);
    const onKey = (e) => { if (e.key === "Escape") setMenuAt(null); };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, [menuAt]);

  const openMenu = (e) => {
    e.stopPropagation();
    const r = btnRef.current.getBoundingClientRect();
    setMenuAt({
      top: r.bottom + 6,
      left: Math.max(8, Math.min(r.left - 210, window.innerWidth - 270)),
    });
  };

  const body = () => {
    /* Always registry.render — including Beat the Yeti — so Scoped* inherit /
       board Viewing match Home. Leaderboard across-mode is handled inside its
       own render via s.across (board: {} is editor metadata only). */
    const draw = def?.render;
    if (!draw) return null;

    /* All Department Metrics reads an empty workspaceIds as "every workspace"
       (DeptMetrics/data.js resolveRows) — the single-workspace fallback below
       is for cards that read workspaceIds as "this card's host workspace",
       which would otherwise collapse its rows down to just the one. */
    const noHostFallback = def?.deptSetup ? [] : fallbackWs;
    const pickedWs = (opts.workspaceIds || []).length
      ? opts.workspaceIds.map(String)
      : noHostFallback;

    const ctx = {
      kpis: filterLiveKpis(data.kpis || []),
      kpisAll: data.kpisAll || data.kpis || [],
      isRealAdmin,
      periodIdx: PERIOD_IDX[period] ?? 3,
      bonus,
      primary: primaryColor || "#1B598E",
      heroProps: {},
      periodName: periodOf(period),
      kpisLoading: !!data.loading,
      viewingOwnerId: userId,
      workspaceId: wsId ? String(wsId) : null,
      editingId: editing && onEdit ? widget.comp : null,
      boardWs: boardWs || null,
      boardWindow: boardWindow || null,
      boardCustom: boardCustom || null,
      globalRange: boardCustom || null,
      inheritWorkspaceIds: Array.isArray(inheritWorkspaceIds) ? inheritWorkspaceIds : [],
      saveWidgetSettings,
      /* Every component on this board, so a card that reads another (Revenue,
         Expenses & Labor takes its Labor from a Payroll vs Revenue card) can
         find and name its siblings. Minus this card itself — nothing should
         be able to point at its own output as an input. */
      boardWidgets: (Array.isArray(boardWidgets) ? boardWidgets : []).filter((w) => w.id !== widget.i),
    };

    const s = {
      ...opts,
      title: widget.title || opts.title || "",
      scene: opts.scene || def.defaultScene || null,
      hide: opts.hide || {},
      workspaceIds: pickedWs,
    };
    return draw(ctx, s, widget.i);
  };

  const label = def?.title || "Component";

  return (
    <div className={`wc wc-component ${editing ? "editing" : ""}`}>
      {editing && (
        <div className="wc-drag wc-comp-drag">
          <IcGrip />
          <span>
            {label}
            {ws && !across && !followsSelected ? ` · ${ws.name}` : ""}
          </span>
        </div>
      )}
      <button
        ref={btnRef}
        type="button"
        className="wc-menu-btn wc-comp-menu db-desktop-only"
        onClick={openMenu}
        aria-label="Component options"
      >
        <IcDots />
      </button>
      <div className="wc-comp-body">{body()}</div>

      {menuAt && createPortal(
        <div ref={popRef} className="db-pop" style={{ top: menuAt.top, left: menuAt.left }}>
          {onEdit && (
            <button type="button" onClick={() => { onEdit(); setMenuAt(null); }}>
              <IcEdit />
              Edit component
            </button>
          )}
          <button type="button" onClick={() => { onDuplicate(); setMenuAt(null); }}>
            <IcCopy />
            Duplicate
          </button>
          {onCopyToDashboard && (
            <button type="button" onClick={() => { onCopyToDashboard(); setMenuAt(null); }}>
              <IcDashboard />
              Copy to dashboard
            </button>
          )}
          <div className="sep" />
          <button type="button" className="danger" onClick={() => { onRemove(); setMenuAt(null); }}>
            <IcTrash />
            Remove
          </button>
        </div>,
        document.body,
      )}
    </div>
  );
}

ComponentCard.propTypes = {
  widget: PropTypes.object.isRequired,
  period: PropTypes.string,
  editing: PropTypes.bool,
  onEdit: PropTypes.func,
  onDuplicate: PropTypes.func.isRequired,
  onCopyToDashboard: PropTypes.func,
  onRemove: PropTypes.func.isRequired,
  boardWs: PropTypes.string,
  boardWindow: PropTypes.string,
  boardCustom: PropTypes.object,
  inheritWorkspaceIds: PropTypes.arrayOf(PropTypes.string),
  saveWidgetSettings: PropTypes.func,
  boardWidgets: PropTypes.array,
};
